/**
 * useLiveLeg — the representative's side of the live officer-directed leg (SV-5-2, DEC-089 ruling 8). The svapp's
 * counterpart of the droom's `useLiveCall`, built on the SAME LiveKit transport (media/livekit.ts) and the same
 * discipline: the grant (wss URL + token) is minted server-side per join, the local camera + mic are published on
 * connect, teardown stops them, and a media-permission denial is surfaced calmly rather than crashing the call.
 *
 * What is different from the droom is only the choreography around the join:
 *   • WAIT honestly — `POST /live/join` answers 409 SV_LIVE_NOT_CLAIMED with `retryAfterS` until an officer takes
 *     the leg; the hook re-asks on that cadence (never a hot loop, never a fabricated "connecting").
 *   • HEARTBEAT every 10 s (`POST /live/heartbeat`) from the moment the step opens — this is what stamps
 *     `customer_presence_at` so the officer's claim can stop treating the representative as UNKNOWN next release —
 *     and it carries the leg's state of record plus the officer's instruction prompts (WIRE_CONTRACT_SV52 §3): the
 *     ONE active row is the instruction card, acked ONCE per promptId the first time a beat carries it (ack-on-show);
 *     the representative's "Done" tap posts the answer and the list is replaced from the response (`promoted` = the
 *     next queued prompt now issued), then a fresh beat re-syncs.
 *   • RE-CONSENT before the join — `POST /live/join` answers 409 SV_CONSENT_LIVE_REQUIRED until the case holds a
 *     consent granting the three live purposes; the hook parks on `consentRequired` (no retry loop) and re-asks to
 *     join only after `consentRecorded()`.
 *   • END clearly — the room going away (ROOM_DELETED / PARTICIPANT_REMOVED), a heartbeat reading ENDED/FAILED, or a
 *     join refused SV_LIVE_ENDED all land on the one `ended` phase.
 *   • A network drop re-joins the SAME room through the same grant path (a fresh mint), like the droom's resume.
 *
 * No real room in tests: the room factory and the two API calls are injectable (`deps`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomEvent, Track } from 'livekit-client';
import {
  ackLivePrompt,
  answerLivePrompt,
  isActiveLivePrompt,
  joinLive,
  liveHeartbeat,
  type LiveAnswerResponse,
  type LiveJoinGrant,
  type LiveHeartbeat,
  type LivePrompt,
} from '@/api/capture';
import { SvApiError } from '@/api/errors';
import { createLiveSession, realRoomFactory, type LiveRoomLike, type LiveSession, type RoomFactory } from './livekit';

export const HEARTBEAT_INTERVAL_MS = 10_000;
/** The fallback cadence when a 409 arrives without `retryAfterS` (the be always sends it; belt only). */
const DEFAULT_RETRY_AFTER_S = 5;
/** Identity prefix of the officer side — mirrors the be `LiveCallRole` (`agent-`); the recorder's `EG_` never counts. */
const OFFICER_IDENTITY_PREFIX = 'agent-';

export type LiveLegPhase =
  /** Asking to join; no officer has taken the leg yet (the honest wait). */
  | { readonly kind: 'waiting'; readonly attempts: number }
  /** The grant arrived; connecting + publishing camera and mic. */
  | { readonly kind: 'connecting' }
  /** On the call. `reconnecting` while the transport heals a blip. */
  | { readonly kind: 'live'; readonly reconnecting: boolean }
  /** The officer ended the session (room gone, or the record says ENDED/FAILED). */
  | { readonly kind: 'ended' }
  /** The case has no live leg to join (never escalated, or since withdrawn). */
  | { readonly kind: 'unavailable' }
  /** The leg was opened on another device — this one is the stale copy. */
  | { readonly kind: 'superseded' }
  /** The backend could not be reached; a retry is offered. */
  | { readonly kind: 'unreachable' }
  /** The representative hung up. */
  | { readonly kind: 'left' }
  /** The join was refused SV_CONSENT_LIVE_REQUIRED: the live items need the representative's consent first. */
  | { readonly kind: 'consentRequired' };

/** A track that can be attached to a media element (both LiveKit local and remote video tracks satisfy this). */
export interface AttachableTrack {
  attach(el: HTMLMediaElement): unknown;
  detach(el?: HTMLMediaElement): unknown;
}

export interface LiveLegDeps {
  readonly join: () => Promise<LiveJoinGrant>;
  readonly heartbeat: () => Promise<LiveHeartbeat>;
  readonly ack: (promptId: string) => Promise<LivePrompt>;
  readonly answer: (promptId: string) => Promise<LiveAnswerResponse>;
  readonly roomFactory: RoomFactory;
}

const DEFAULT_DEPS: LiveLegDeps = {
  join: joinLive,
  heartbeat: liveHeartbeat,
  ack: ackLivePrompt,
  answer: answerLivePrompt,
  roomFactory: realRoomFactory,
};

export interface LiveLeg {
  readonly phase: LiveLegPhase;
  readonly prompts: readonly LivePrompt[];
  readonly officerPresent: boolean;
  readonly localVideoTrack: AttachableTrack | null;
  readonly remoteVideoTrack: AttachableTrack | null;
  readonly remoteAudioTrack: AttachableTrack | null;
  /** Calm, representative-facing camera/mic message key (never a stack trace); null when media is fine. */
  readonly mediaErrorKey: 'permission' | 'other' | null;
  readonly leave: () => void;
  /** After `unreachable`: ask again. */
  readonly retry: () => void;
  /** The "Done" tap on the active instruction card — posts the answer, replaces the list from the response, re-beats. */
  readonly answer: (promptId: string) => Promise<void>;
  /** The promptId whose answer is in flight (the Done button is disabled for it), else null. */
  readonly answering: string | null;
  /** After `consentRequired`: the ceremony recorded the live consent — back to waiting and ask to join again. */
  readonly consentRecorded: () => void;
}

/** Replace the answered row and append the promoted one (when present and not already listed). */
export function applyAnswer(prev: readonly LivePrompt[], res: LiveAnswerResponse): readonly LivePrompt[] {
  const answered = res.prompt;
  let next: LivePrompt[] = answered
    ? prev.map((p) => (p.promptId === answered.promptId ? answered : p))
    : [...prev];
  if (answered && !next.some((p) => p.promptId === answered.promptId)) next = [...next, answered];
  const promoted = res.promoted ?? null;
  if (promoted && !next.some((p) => p.promptId === promoted.promptId)) next = [...next, promoted];
  return next;
}

function isOfficer(identity: string | undefined): boolean {
  return typeof identity === 'string' && identity.startsWith(OFFICER_IDENTITY_PREFIX);
}

function isPermissionDenial(err: unknown): boolean {
  const name = (err as { name?: string } | undefined)?.name;
  return name === 'NotAllowedError' || name === 'NotFoundError' || name === 'NotReadableError'
    || name === 'OverconstrainedError' || name === 'SecurityError';
}

export function useLiveLeg(enabled: boolean, deps: LiveLegDeps = DEFAULT_DEPS): LiveLeg {
  const [phase, setPhase] = useState<LiveLegPhase>({ kind: 'waiting', attempts: 0 });
  const [prompts, setPrompts] = useState<readonly LivePrompt[]>([]);
  const [officerPresent, setOfficerPresent] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState<AttachableTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<AttachableTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<AttachableTrack | null>(null);
  const [mediaErrorKey, setMediaErrorKey] = useState<'permission' | 'other' | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [answering, setAnswering] = useState<string | null>(null);

  const sessionRef = useRef<LiveSession | null>(null);
  const depsRef = useRef(deps);
  depsRef.current = deps;
  /** promptIds already acked on this device — ack-on-show happens ONCE per prompt, across every beat. */
  const ackedRef = useRef<Set<string>>(new Set());
  /** The current effect's beat, so `answer` can re-sync straight after the post. */
  const beatRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let disposed = false;
    let joinTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let terminal = false;

    const finish = (next: LiveLegPhase): void => {
      terminal = true;
      if (joinTimer) clearTimeout(joinTimer);
      sessionRef.current?.disconnect();
      sessionRef.current = null;
      if (!disposed) setPhase(next);
    };

    const syncRemote = (room: LiveRoomLike): void => {
      if (disposed) return;
      let officer = false;
      room.remoteParticipants.forEach((p) => {
        if (isOfficer(p.identity)) officer = true;
      });
      setOfficerPresent(officer);
    };

    const connectWith = async (grant: LiveJoinGrant): Promise<void> => {
      const lk = createLiveSession((s) => {
        if (disposed || terminal) return;
        if (s === 'connected') setPhase({ kind: 'live', reconnecting: false });
        else if (s === 'reconnecting') setPhase({ kind: 'live', reconnecting: true });
        else if (s === 'ended') finish({ kind: 'ended' });
        else if (s === 'superseded') finish({ kind: 'superseded' });
        else if (s === 'left') finish({ kind: 'left' });
        else if (s === 'dropped') {
          // A drop is not an ending: re-join the SAME room through a fresh grant (the droom's resume model).
          sessionRef.current = null;
          setRemoteVideoTrack(null);
          setRemoteAudioTrack(null);
          setPhase({ kind: 'live', reconnecting: true });
          void askToJoin();
        }
      }, depsRef.current.roomFactory);
      sessionRef.current = lk;
      const room = lk.room;
      room.on(RoomEvent.ParticipantConnected, (() => syncRemote(room)) as (...args: never[]) => void);
      room.on(RoomEvent.ParticipantDisconnected, (() => {
        syncRemote(room);
        if (room.remoteParticipants.size === 0) {
          setRemoteVideoTrack(null);
          setRemoteAudioTrack(null);
        }
      }) as (...args: never[]) => void);
      room.on(RoomEvent.TrackSubscribed, ((track: { kind: string } & AttachableTrack) => {
        if (disposed) return;
        syncRemote(room);
        if (track.kind === Track.Kind.Video) setRemoteVideoTrack(track);
        else if (track.kind === Track.Kind.Audio) setRemoteAudioTrack(track);
      }) as (...args: never[]) => void);
      room.on(RoomEvent.TrackUnsubscribed, ((track: { kind: string }) => {
        if (disposed) return;
        if (track.kind === Track.Kind.Video) setRemoteVideoTrack(null);
        else if (track.kind === Track.Kind.Audio) setRemoteAudioTrack(null);
      }) as (...args: never[]) => void);

      setPhase({ kind: 'connecting' });
      await lk.connect(grant);
      if (disposed || terminal) return;
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        await room.localParticipant.setCameraEnabled(true);
        if (disposed) return;
        setMediaErrorKey(null);
        const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        setLocalVideoTrack((camPub?.videoTrack as AttachableTrack | undefined) ?? null);
        syncRemote(room);
      } catch (mediaErr) {
        // Only a real getUserMedia denial means "allow access"; anything else is a transport-side hiccup and
        // must not send the representative into their browser settings.
        if (!disposed) setMediaErrorKey(isPermissionDenial(mediaErr) ? 'permission' : 'other');
      }
    };

    const askToJoin = async (): Promise<void> => {
      if (disposed || terminal) return;
      attempts += 1;
      try {
        const grant = await depsRef.current.join();
        if (disposed || terminal) return;
        await connectWith(grant);
      } catch (e) {
        if (disposed || terminal) return;
        if (e instanceof SvApiError && e.code === 'SV_LIVE_NOT_CLAIMED') {
          const details = e.details as { retryAfterS?: unknown } | null;
          const retryAfterS = typeof details?.retryAfterS === 'number' ? details.retryAfterS : DEFAULT_RETRY_AFTER_S;
          setPhase({ kind: 'waiting', attempts });
          joinTimer = setTimeout(() => void askToJoin(), Math.max(1, retryAfterS) * 1000);
        } else if (e instanceof SvApiError && e.code === 'SV_LIVE_ENDED') {
          finish({ kind: 'ended' });
        } else if (e instanceof SvApiError && e.code === 'SV_LIVE_NOT_REQUESTED') {
          finish({ kind: 'unavailable' });
        } else if (e instanceof SvApiError && e.code === 'SV_CONSENT_LIVE_REQUIRED') {
          // Not a wait: the representative must consent to the live items first. Presence beats continue; the join
          // is re-asked only from consentRecorded().
          setPhase({ kind: 'consentRequired' });
        } else {
          setPhase({ kind: 'unreachable' });
        }
      }
    };

    const ack = async (promptId: string): Promise<void> => {
      try {
        const row = await depsRef.current.ack(promptId);
        if (disposed || terminal) return;
        // The response is the same row, state ACKNOWLEDGED — only the state is taken from it; the served text and
        // kind stay what the heartbeat carried (never let a thin response blank the card).
        const state = row && typeof row.state === 'string' ? row.state : 'ACKNOWLEDGED';
        setPrompts((prev) => prev.map((p) => (p.promptId === promptId && isActiveLivePrompt(p) ? { ...p, state } : p)));
      } catch (e) {
        // A refusal (the row is already closed, unknown) is final; a transport fault lets the next beat try again.
        if (!(e instanceof SvApiError)) ackedRef.current.delete(promptId);
      }
    };

    const beat = async (): Promise<void> => {
      if (disposed || terminal) return;
      try {
        const hb = await depsRef.current.heartbeat();
        if (disposed || terminal) return;
        const raw = Array.isArray(hb.prompts) ? hb.prompts : [];
        // A PENDING row this device already acked is shown ACKNOWLEDGED even if the beat raced the ack (idempotent).
        const served = raw.map((p) => (p.state === 'PENDING' && ackedRef.current.has(p.promptId) ? { ...p, state: 'ACKNOWLEDGED' } : p));
        setPrompts(served);
        if (hb.state === 'ENDED' || hb.state === 'FAILED') {
          finish({ kind: 'ended' });
          return;
        }
        // Ack-on-show: the single PENDING row is acknowledged once, the first beat that carries it.
        const pending = raw.find((p) => p.state === 'PENDING');
        if (pending && !ackedRef.current.has(pending.promptId)) {
          ackedRef.current.add(pending.promptId);
          void ack(pending.promptId);
        }
      } catch (e) {
        // A heartbeat that fails is presence lost for one beat, never a dead end — except the record saying so.
        if (e instanceof SvApiError && (e.code === 'SV_LIVE_ENDED' || e.code === 'SV_LIVE_NOT_REQUESTED')) {
          finish({ kind: e.code === 'SV_LIVE_ENDED' ? 'ended' : 'unavailable' });
        }
      }
    };

    beatRef.current = beat;
    void beat();
    const beatTimer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);
    void askToJoin();

    return () => {
      disposed = true;
      beatRef.current = null;
      clearInterval(beatTimer);
      if (joinTimer) clearTimeout(joinTimer);
      sessionRef.current?.disconnect(); // stops + unpublishes camera/mic — no getUserMedia handle outlives the step
      sessionRef.current = null;
    };
  }, [enabled, retryNonce]);

  const leave = useCallback(() => {
    sessionRef.current?.disconnect();
    sessionRef.current = null;
    setPhase({ kind: 'left' });
  }, []);

  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

  const consentRecorded = useCallback(() => {
    setPhase({ kind: 'waiting', attempts: 0 });
    setRetryNonce((n) => n + 1);
  }, []);

  const answer = useCallback(async (promptId: string): Promise<void> => {
    setAnswering(promptId);
    try {
      const res = await depsRef.current.answer(promptId);
      setPrompts((prev) => applyAnswer(prev, res));
    } catch {
      // Already closed (409 SV_LIVE_PROMPT_CLOSED), unknown, or offline — the re-beat below re-syncs the list.
    } finally {
      setAnswering((cur) => (cur === promptId ? null : cur));
    }
    await beatRef.current?.();
  }, []);

  return {
    phase,
    prompts,
    officerPresent,
    localVideoTrack,
    remoteVideoTrack,
    remoteAudioTrack,
    mediaErrorKey,
    leave,
    retry,
    answer,
    answering,
    consentRecorded,
  };
}
